import type { VideoContent } from '@mawkaa/types';
import { PlayCircle } from 'lucide-react';

interface VideoBlockProps {
  content: VideoContent;
  isEditing?: boolean;
}

export function VideoBlock({ content, isEditing }: VideoBlockProps) {
  const { url, title, autoplay = false, controls = true } = content;

  // Extract YouTube video ID
  const getYouTubeId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  // Extract Vimeo video ID
  const getVimeoId = (url: string) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
  };

  if (!url) {
    return (
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <PlayCircle className="w-16 h-16 mx-auto mb-2" />
              <p>Ajoutez une URL vidéo (YouTube, Vimeo)</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const youtubeId = getYouTubeId(url);
  const vimeoId = getVimeoId(url);

  return (
    <section className="px-4 py-12">
      <div className="max-w-4xl mx-auto">
        {title && <h2 className="text-2xl font-bold text-center mb-6">{title}</h2>}
        <div className="aspect-video rounded-lg overflow-hidden">
          {youtubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?autoplay=${autoplay ? 1 : 0}&controls=${controls ? 1 : 0}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : vimeoId ? (
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=${autoplay ? 1 : 0}`}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={url}
              controls={controls}
              autoPlay={autoplay && !isEditing}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      </div>
    </section>
  );
}
